<template>
  <b-container fluid class="project-detail-wrapper">
    <LoadingIndicator 
      v-if="isNewlyLoadingProject"
      topic="Project"
    />
    <ProjectMissingDetailNotice 
      v-if="shouldShowMissingDetailsMessage"
      :project="project"
      :missingDetails="missingDetails"
      @getProjectDetails="getProjectDetails"
    />
    <BaseMessageBlock
      v-if="hasProjectError"
      :messages="[{ message: 'An error occured. We were unable to load this project.' }]"
    />
    <div v-if="shouldShowProjectHeader">
      <ProjectHeader 
        :project="project"
        @addMeeting="addMeeting"
        @showMeetNowPrompt="showMeetNowPrompt"
      />
      <ProjectNavigation
        :isKickoffCompleted="isKickoffCompleted" 
      />
    </div>
    <div v-if="shouldShowProjectContent" class="pt-4">
      <router-view
        :projectSummary="projectSummary"
      />
      <!-- <router-view /> -->
      <ProjectWizardAddUsers
        v-if="!hasAddedUsers"
        :projectId="projectId"
        :projectName="projectName"
        @submit="$router.push(`/project/${projectId}/users`)"
      />
      <ProjectWizardHoldKickoff
        v-if="isKickoffCreated && !isKickoffCompleted"
        :projectId="projectId"
        :kickoff="kickoff"
      />
      <ProjectWizardSendCharter
        v-if="isKickoffCompleted && !hasSentCharter"
        :projectId="projectId"
        @submit="$router.push(`/project/${projectId}/charter`)"
      />
      <ProjectWizardCreateSurvey
        v-if="hasSentCharter && !hasCreatedSurvey"
        :projectId="projectId"
        @submit="$router.push(`/project/${projectId}/evaluation`)"
      />
      <ProjectWizardScheduleKickoff
        ref="ProjectWizardScheduleKickoff"
        :projectId="projectId"
        @submit="addMeeting"
      />
      <AddMeetingModal
        ref="AddMeetingModal"
        @submit="addMeetingSubmit"
        :projectId="projectId"
        :users="projectUsers"
        :isKickoffCreated="isKickoffCreated"
      />
      <MeetNowModal
        ref="MeetNowModal"
        :projectId="projectId"
        :isKickoffCreated="isKickoffCreated"
      />
    </div>
</b-container>
</template>

<script>
import axios from "axios";
import { mapGetters } from "vuex";
import ProjectMissingDetailNotice from "@/components/ProjectMissingDetailNotice.vue";
import ProjectHeader from "@/components/ProjectHeader.vue";
import ProjectNavigation from "@/components/ProjectNavigation.vue";
import ProjectWizardAddUsers from "@/components/ProjectWizardAddUsers.vue";
import ProjectWizardScheduleKickoff from "@/components/ProjectWizardScheduleKickoff.vue";
import ProjectWizardHoldKickoff from "@/components/ProjectWizardHoldKickoff.vue";
import ProjectWizardCreateSurvey from "@/components/ProjectWizardCreateSurvey.vue";
import ProjectWizardSendCharter from "@/components/ProjectWizardSendCharter.vue";
import AddMeetingModal from "@/modal-components/AddMeetingModal.vue";
import MeetNowModal from "@/components/project/MeetNowModal.vue";

export default {
  components: {
    ProjectMissingDetailNotice,
		ProjectHeader,
		ProjectNavigation,
    ProjectWizardAddUsers,
    ProjectWizardScheduleKickoff,
    ProjectWizardHoldKickoff,
    ProjectWizardCreateSurvey,
    ProjectWizardSendCharter,
		AddMeetingModal,
		MeetNowModal,
  },
  data() {
    return {
      isNewToProjectPage: false,
			projectSummary: null,
      // project: null,
      projectName: '',
			missingDetails: [],
			errors: []
    }
  },
  computed: {
    ...mapGetters({
      isLoading: 'getDataLoading',
      hasLoaded: 'getDataLoaded',
    }),
		projectId() {
			return this.$route.params.id;
		},
		project() {
			return this.projectSummary ? this.projectSummary.project : null;
		},
		meetings() {
			return this.projectSummary ? this.projectSummary.meetings : [];
		},
		projectUsers() {
			return this.projectSummary ? this.projectSummary.projectUsers : [];
		},
		isMissingDetails() {
      return this.missingDetails.length > 0;
    },
    kickoff() {
      return this.meetings.filter(meeting => meeting.type === "Kick-Off Meeting")[0];
    },
    isKickoffCreated() {
      return this.meetings.filter(meeting => meeting.type === "Kick-Off Meeting").length > 0;
    },
    isKickoffCompleted() {
      return this.kickoff && this.kickoff.status === "Closed";
    },
    hasAddedUsers() {
      return this.projectUsers.length > 2;
    },
    hasCreatedSurvey() {
      return this.projectSummary && 
        this.projectSummary.project.survey.questions.length > 0;
    },
    hasSentCharter() {
      const pastCharterRecipients = this.projectSummary.charterRelations.filter(user => user.charterSentDate);
      return pastCharterRecipients.length > 0;
    },
    charterSignees() {
      const approvedCharters = this.projectSummary.charterRelations.filter(user => user.approveCharterDate);
      
      return approvedCharters.map(signee => {
        return this.projectUsers.find(user => user.userId === signee.userId);
      });
    },
    usersWithCharterDate() {
      const approvedCharters = this.projectSummary.charterRelations.filter(user => user.approveCharterDate);
      

      const usersWithCharterDate = this.projectUsers.map(user => {
        const matchingCharter = approvedCharters.find(charter => user.userId === charter.userId);

        return {
          ...user,
          approveCharterDate: matchingCharter ? matchingCharter.approveCharterDate : null
        }
      });

      return usersWithCharterDate;
    },
    isNewlyLoadingProject() {
      return this.isNewToProjectPage && this.isLoading;
    },
    shouldShowMissingDetailsMessage() {
      return this.hasLoaded && this.project && this.isMissingDetails;
    },
    shouldShowProjectHeader() {
      return this.hasLoaded && this.project && !this.isMissingDetails;
    },
    shouldShowProjectContent() {
      return this.hasLoaded && this.project && !this.isMissingDetails;
    },
    hasProjectError() {
      return this.hasLoaded && !this.project;
    }
  },
  watch: {
    $route() {
      this.getProjectDetails();
    }
  },
  mounted() {
    this.isNewToProjectPage = true;
    this.$store.dispatch('resetLoading');
    if (!this.projectId && this.accountUser) {
      this.redirectUserIfNoProjectId();
    }
		this.getProjectDetails();
		// this.loadAllOrganizations();
  },
  methods: {
		redirectUserIfNoProjectId() {
			let adminProjects = this.accountUser.projectPermissions.filter(
        (p) => p.level === "Admin"
      );
      if (adminProjects.length == 0) {
        return this.defaultRedirect();
      }
      const id = adminProjects[0].id;
      return this.$router.push({ name: "ProjectPages", params: { id: id } });
		},
    getProjectDetails() {
      let url = `${this.$api_url}/projects/${this.projectId}/summary?guest=false`;
      
      axios
        .get(url, this.getReqConfig())
        .then((response) => {
					this.projectSummary = response.data;
          // this.project = this.projectSummary.project;
          this.projectName = response.data.project.projectName;
          this.identifyMissingDetails();
          // if (!this.isMissingDetails) {
          //   this.$emit('projectLoaded', this.projectSummary);
          // }
          // this.showKickoffWizardIfHaveUsers();
        })
        .catch((err) => {
          this.$store.dispatch('dataLoaded');
          if (err.response && err.response.statusText) {
            this.errors.push({ message: err.response.statusText });
          } else {
            this.errors.push(err);
          }

          if (err.response || err.request) {
            // this.defaultRedirect();

          }
        })
        .finally(() => {
          this.$store.dispatch('dataLoaded');
          this.isNewToProjectPage = false;
        });
    },
		// loadAllOrganizations() {
    //   axios
    //     .get(`${this.$api_url}/organizations`, this.getReqConfig())
    //     .then((response) => {
    //       this.allOrganizations = response.data;
    //     })
    //     .catch((err) => {
    //       this.errors.push(...this.$helpers.formatErrors(err));
    //     });
    // },
    hasPrimaryOwner(project) {
      return Boolean(project.primaryOwner);
    },
    hasprimaryContractor(project) {
      return Boolean(project.primaryContractor);
    },
    hasOwnerOrganization(project) {
      return Boolean(project.ownerOrganization);
    },
    hasContractorOrganization(project) {
      return Boolean(project.contractorOrganization);
    },
		identifyMissingDetails() {
      this.missingDetails = [];
      if (!this.hasOwnerOrganization(this.project)) {
        this.missingDetails.push('project-owner');
      }
      if (!this.hasContractorOrganization(this.project)) {
        this.missingDetails.push('project-contractor');
      }
      if (!this.hasPrimaryOwner(this.project)) {
        this.missingDetails.push('project-re');
      }
      if (!this.hasprimaryContractor(this.project)) {
        this.missingDetails.push('project-pm');
      }
    },
		// setupProjectDetails() {
    //   this.surveyId = this.project.survey.surveyId;
    //   this.projectCost = this.project.projectCost;
    //   this.projectDays = this.$formatter.daysFromDates(
    //     this.project.startDate,
    //     this.project.endDate
    //   );
    //   this.periodName = this.projectSummary.periodName;
    //   this.periods = this.projectPeriods;

    //   this.periodDate = new Date(this.projectSummary.periodName);
    //   this.byQuestionData = this.$helpers.mapDataPointToChartDataSet(
    //     this.projectSummary.responsesByQuestion
    //   );

    //   this.comments = this.projectSummary.comments
    //     .sort((a, b) => (a.answerIndex < b.answerIndex ? 1 : -1))
    //     .sort((a, b) => (a.questionText > b.questionText ? 1 : -1));
    //   //Call Another Function to get Users
    //   this.projectUsers = this.projectUsers;
    //   this.loadAllUsers();
    //   this.projectMetrics = this.projectMetrics;
    //   this.documents = this.projectSummary.documents;
    //   this.news = this.projectSummary.news;
    //   this.presetTags = this.project.presetTags;

    //   this.contractorLogo = this.project.primaryContractor.organizationLogo;
    //   this.ownerLogo = this.project.primaryOwner.organizationLogo;

    //   this.meetings = this.projectSummary.meetings;
    //   this.facilitatorSurveys = this.projectSummary.facilitatorSurveys;
    //   this.showKickoffWizardIfHaveUsers();
    // },
		addMeeting() {
			this.$refs.AddMeetingModal.show();
		},
		showMeetNowPrompt() {
      this.$refs.MeetNowModal.show();
    },
		addMeetingSubmit(meeting) {
      this.meetings.unshift(meeting);
      this.$refs.AddMeetingModal.cancel();
    },
    showKickoffWizardIfHaveUsers() {
      if (this.hasAddedUsers && !this.isKickoffCreated) {
        this.$refs.ProjectWizardScheduleKickoff.show();
      }
    },
  }
}
</script>

<style lang="scss" scoped>

</style>
